import React, { useContext, useEffect, useMemo, useRef } from "react";
import { BryntumButton } from "@bryntum/schedulerpro-react";
import { EDITOR_KEYS } from "../../../../../other/constants/EditorKeys.jsx";
import BryntumContext from "../../../../context/bryntumContext.jsx";
import { GRANTS } from "../../../../../other/constants/PrivilegeTypes.jsx";
import {
  IMPORT_TYPES,
  IMPORT_HEADERS,
  APP_HEADER_HOVER_MENU_BUTTON_CLS,
} from "../../../../../other/constants/OtherKeys.jsx";
import { getIsArchivedForBoard, saveIsArchivedForBoard } from "../../../../../other/utils/otherUtils.jsx";

const AppBoardButton = () => {
  const buttonRef = useRef(null);
  const { editorHandler, selectedBoard, boardManager, user } = useContext(BryntumContext);

  useEffect(() => {
    if (selectedBoard) {
      buttonRef.current.instance.text = selectedBoard.name;
    }
  }, [selectedBoard]);

  const isMenuDisabled = user && !user.can(GRANTS.grant_one);

  const boardMenu = useMemo(() => ({
    cls: "swivu-header-menu-rounded",
    itemCls: "swivu-header-menu-item",
    items: {
      share: {
        text: "Share Schedule",
        onItem: () => editorHandler.openEditor(EDITOR_KEYS.shareSchedulerPopup, null, {}, {
          useExisting: true,
          by: "recordType"
        }),
        disabled: !selectedBoard || isMenuDisabled,
      },
      personalSettings: {
        text: "Personal Settings",
        onItem: () => {
          editorHandler.openEditor(
            EDITOR_KEYS.settingPersonalEditor,
            null,
            {},
            { useExisting: true, by: "recordType" }
          );
        },
        disabled: !selectedBoard || !user
      },
      ...(!isMenuDisabled
        ? {
            boardSettings: {
              text: "Board Settings",
              cls: "swivu-menu-divider",
              onItem: () =>
                editorHandler.openEditor(
                  EDITOR_KEYS.settingGeneralEditor,
                  null,
                  {},
                  {
                    useExisting: true,
                    by: "recordType"
                  }
                ),
              disabled: !selectedBoard || isMenuDisabled
            }
          }
        : undefined),
      importMenu: {
        text: "Import/Export",
        cls: "swivu-menu-divider",
        disabled: !selectedBoard || (user && !user.can(GRANTS.grant_three)),
        menu: {
          cls: "swivu-header-menu-rounded",
          itemCls: "swivu-header-menu-item",
          items: [
            {
              text: "Resources",
              disabled: user && !user.can(GRANTS.grant_one),
              onItem: () =>
                editorHandler.openEditor(
                  EDITOR_KEYS.importEditor,
                  null,
                  {
                    importType: IMPORT_TYPES.resources,
                    canEdit: user && user.can(GRANTS.grant_one),
                    importHeader: IMPORT_HEADERS.resources,
                    labelIndex: 12
                  },
                  { useExisting: true, by: "recordType" }
                )
            },
            {
              text: "Tasks",
              disabled: user && !user.can(GRANTS.grant_one),
              onItem: () =>
                editorHandler.openEditor(
                  EDITOR_KEYS.importEditor,
                  null,
                  {
                    importType: IMPORT_TYPES.tasks,
                    canEdit: user && user.can(GRANTS.grant_one),
                    importHeader: IMPORT_HEADERS.tasks
                  },
                  { useExisting: true, by: "recordType" }
                )
            },
            {
              text: "Clients",
              disabled: user && !user.can(GRANTS.grant_one),
              onItem: () =>
                editorHandler.openEditor(
                  EDITOR_KEYS.importEditor,
                  null,
                  {
                    importType: IMPORT_TYPES.clients,
                    canEdit: user && user.can(GRANTS.grant_one),
                    importHeader: IMPORT_HEADERS.clients
                  },
                  { useExisting: true, by: "recordType" }
                )
            },
            {
              text: "Budget",
              disabled: user && (user.isSandboxBoard && user.isDemoUser || !user.can(GRANTS.grant_two)),
              onItem: () =>
                editorHandler.openEditor(
                  EDITOR_KEYS.importEditor,
                  null,
                  {
                    importType: IMPORT_TYPES.taskBudget,
                    canEdit: user && user.can(GRANTS.grant_two),
                    importHeader: IMPORT_HEADERS.taskBudget
                  },
                  { useExisting: true, by: "recordType" }
                )
            },
            {
              text: "Contacts",
              disabled: user && !user.can(GRANTS.grant_one),
              onItem: () =>
                editorHandler.openEditor(
                  EDITOR_KEYS.importEditor,
                  null,
                  {
                    importType: IMPORT_TYPES.contacts,
                    canEdit: user && user.can(GRANTS.grant_one),
                    importHeader: IMPORT_HEADERS.contacts
                  },
                  { useExisting: true, by: "recordType" }
                )
            },
            {
              text: "Board Users",
              disabled: user && (user.isSandboxBoard && user.isDemoUser || !user.can(GRANTS.grant_one)),
              onItem: () =>
                editorHandler.openEditor(
                  EDITOR_KEYS.importEditor,
                  null,
                  {
                    importType: IMPORT_TYPES.boardUsers,
                    canEdit: user && user.can(GRANTS.grant_one),
                    importHeader: IMPORT_HEADERS.boardUsers
                  },
                  { useExisting: true, by: "recordType" }
                )
            }
          ]
        }
      },
      showArchive: {
        type: "checkbox",
        text: "Show Archive",
        cls: "show-archive-widget b-menuitem",
        checked: getIsArchivedForBoard(selectedBoard?.record?.slug),
        onChange: ({ checked, source }) => {
          source.parent.close();
          const selectedBoardSlug = selectedBoard.record.slug;
          saveIsArchivedForBoard(selectedBoardSlug, checked);
          setTimeout(() => boardManager.reloadSelectedBoard(), 0);
        },
        disabled: !(selectedBoard && user && user.can(GRANTS.grant_four)),
        readOnly: (user && !user.can(GRANTS.grant_four)),
      }
    },
    width: "17.5em"
  }), [user, editorHandler, selectedBoard]);

  const canShowButton = Boolean(user) && Boolean(selectedBoard);

  if (canShowButton) {
    return (
      <BryntumButton
        ref={buttonRef}
        cls={`swivu-header-menu-button b-transparent ${APP_HEADER_HOVER_MENU_BUTTON_CLS}`}
        text="Loading Board..."
        menu={boardMenu}
        menuIcon={null}
      />
    );
  }
};

export default AppBoardButton;
